// Import necessary components and hooks
import { AutoComplete, Button, Card, Col, Form, InputNumber, Radio, Row, Select, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateField } from '../../../../../contextRedux/Contract/ContractModel';
import { RootState } from '../../../../../store';
import './InterventiiTab.css';

const InterventiiTab: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [globalValue, setGlobalValue] = useState<number | null>(null);
  const [interventionLevel, setInterventionLevel] = useState<
    'contract' | 'workPlace' | 'systemType' | 'workPlacePeriod' | 'workPlaceSystemTypePeriod'
  >('contract');

  const reduxContract = useSelector((state: RootState) => state?.models?.contract);
  const contractInterventionTypes = useSelector((state: RootState) => state?.store.contractInterventionType.data);
  const contractInterventionTariffTypes = useSelector(
    (state: RootState) => state?.store.contractInterventionTariffType.data
  );

  const allWorkplaces = Array.from(
    new Set(reduxContract.contractWorkPlaceItems?.map((item: any) => item.workPlace?.name))
  ).filter(Boolean);

  useEffect(() => {
    // Initialize form with values from redux
    form.setFieldsValue({
      contractInterventionSetting: {
        enumContractInterventionType: reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId,
        enumContractInterventionTariffType:
          reduxContract?.contractInterventionSetting?.enumContractInterventionTariffTypeId,
        interventionLevel: reduxContract?.contractInterventionSetting?.interventionLevel,
        customHourlyRate: reduxContract?.contractInterventionSetting?.customHourlyRate,
        freeHours: reduxContract?.contractInterventionSetting?.freeHoursLimit,
        freeInterventionsCount: reduxContract?.contractInterventionSetting?.freeInterventionsCount,
        contractWorkPlaceInterventions: reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.map(
          (intervention: any) => intervention.workPlace?.name
        ),
      },
    });

    // Set intervention level from redux if available
    if (reduxContract?.contractInterventionSetting?.interventionLevel) {
      setInterventionLevel(reduxContract.contractInterventionSetting.interventionLevel);
    }
  }, [reduxContract]);

  // Add useEffect to handle systemType workplace selection
  useEffect(() => {
    if (interventionLevel === 'systemType') {
      // Get all unique workplaces from contractWorkPlaceItems
      const allWorkPlaceNames = Array.from(
        new Set(reduxContract.contractWorkPlaceItems.map((item: any) => item.workPlace?.name))
      ).filter(Boolean);

      // Call handleWorkPlaceChange with all workplaces
      handleWorkPlaceChange(allWorkPlaceNames);
    }
  }, [interventionLevel]);

  const handleWorkPlaceChange = (workPlaceNames: string[]) => {
    // Handle "Selectează Toate" option
    if (workPlaceNames.includes('all')) {
      if (workPlaceNames.length === allWorkplaces.length + 1) {
        workPlaceNames = [];
      } else {
        workPlaceNames = allWorkplaces;
      }
    } else {
      if (workPlaceNames.length === allWorkplaces.length) {
        workPlaceNames = ['all', ...workPlaceNames];
      }
    }

    // Get selected workplaces
    const selectedWorkPlaces = reduxContract.company.workPlaces.filter((w: any) => workPlaceNames.includes(w.name));

    // Create new interventions based on the current intervention level
    let newInterventions: any[] = [];

    if (interventionLevel === 'workPlace') {
      // One intervention per selected workplace
      newInterventions = selectedWorkPlaces.map((workPlace: any) => ({
        id: '',
        contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
        workPlaceId: workPlace.id,
        workPlace: workPlace,
        systemTypeId: '',
        freeInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
          (cwpi: any) => cwpi.workPlaceId == workPlace?.id
        )?.freeInterventionsCount,
        usedInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
          (cwpi: any) => cwpi.workPlaceId == workPlace?.id
        )?.usedInterventionsCount,
        freeHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
          (cwpi: any) => cwpi.workPlaceId == workPlace?.id
        )?.freeHours,
        usedHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
          (cwpi: any) => cwpi.workPlaceId == workPlace?.id
        )?.usedHours,
      }));
    } else if (interventionLevel === 'systemType') {
      const selectedWpIds = new Set(selectedWorkPlaces.map((wp: any) => wp.id));

      // 2. Group by systemTypeId
      const uniqueBySystem = new Map<string, any>();

      reduxContract.contractWorkPlaceItems.forEach((item: any) => {
        const wpId = item.workPlace?.id;
        const sys = item.systemType;
        const sysId = sys?.id;

        // only consider items for selected workplaces
        if (wpId && sysId && selectedWpIds.has(wpId)) {
          if (!uniqueBySystem.has(sysId)) {
            // first time we see this systemType → create a new "intervention" stub
            uniqueBySystem.set(sysId, {
              id: '', // new record
              contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
              workPlaceId: wpId, // not needed at systemType level
              workPlace: item.workPlace, // not needed at systemType level
              systemTypeId: sysId,
              systemType: sys,
              freeInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.systemTypeId == sysId
              )?.freeInterventionsCount,
              usedInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.systemTypeId == sysId
              )?.usedInterventionsCount,
              freeHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.systemTypeId == sysId
              )?.freeHours,
              usedHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.systemTypeId == sysId
              )?.usedHours,
              period: undefined, // not used here
            });
          }
        }
      });

      // 3. Convert the Map values into an array
      newInterventions = Array.from(uniqueBySystem.values());
    } else if (interventionLevel === 'workPlacePeriod') {
      // For each selected workplace, find all unique periods from contractWorkPlaceItems
      const uniqueCombinations = new Map();
      selectedWorkPlaces.forEach((workPlace: any) => {
        const periods = Array.from(
          new Set(
            reduxContract.contractWorkPlaceItems
              .filter((item: any) => item.workPlace?.id === workPlace.id)
              .map((item: any) => item.period)
          )
        ).filter(Boolean);

        periods.forEach((period: string) => {
          const key = `${workPlace.id}-${period}`;
          if (!uniqueCombinations.has(key)) {
            uniqueCombinations.set(key, {
              id: '',
              contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
              workPlaceId: workPlace.id,
              workPlace: workPlace,
              period,
              freeInterventionsCount: 0,
              usedInterventionsCount: 0,
              freeHours: 0,
              usedHours: 0,
            });
          }
        });
      });
      newInterventions = Array.from(uniqueCombinations.values());
    } else if (interventionLevel === 'workPlaceSystemTypePeriod') {
      // Get unique combinations of workplace + system type + period from contractWorkPlaceItems for selected workplaces
      const uniqueCombinations = new Map();
      reduxContract.contractWorkPlaceItems.forEach((item: any) => {
        if (selectedWorkPlaces.some((wp: any) => wp.id === item.workPlace?.id)) {
          const key = `${item.workPlace.id}-${item.systemType.id}-${item.period}`;
          if (!uniqueCombinations.has(key)) {
            uniqueCombinations.set(key, {
              id: '',
              contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
              workPlaceId: item.workPlace.id,
              workPlace: item.workPlace,
              systemTypeId: item.systemType.id,
              systemType: item.systemType,
              period: item.period,
              freeInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
              usedInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
              freeHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
              usedHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
            });
          }
        }
      });
      newInterventions = Array.from(uniqueCombinations.values());
    }

    // Update Redux with new interventions
    dispatch(
      updateField({
        field: 'contractInterventionSetting',
        value: {
          ...reduxContract.contractInterventionSetting,
          contractId: reduxContract.id ?? '',
          contractWorkPlaceInterventions: newInterventions,
        },
      })
    );
  };

  const handleInterventionTypeChange = (value: string) => {
    const selected = contractInterventionTypes.find((type: any) => type.name === value);
    dispatch(
      updateField({
        field: 'contractInterventionSetting',
        value: {
          ...reduxContract.contractInterventionSetting,
          contractId: reduxContract.id ?? '',
          enumContractInterventionTypeId: selected?.id,
          enumContractInterventionType: selected,
        },
      })
    );
  };

  const handleTariffChange = (value: string) => {
    const selected = contractInterventionTariffTypes.find((item: any) => item.name === value);
    dispatch(
      updateField({
        field: 'contractInterventionSetting',
        value: {
          ...reduxContract.contractInterventionSetting,
          contractId: reduxContract.id ?? '',
          enumContractInterventionTariffTypeId: selected?.id,
          enumContractInterventionTariffType: selected,
          customHourlyRate:
            selected?.id === 3 || selected?.id === 4
              ? reduxContract.contractInterventionSetting?.customHourlyRate
              : 180,
        },
      })
    );
  };

  const handleWorkPlaceInterventionChange = (
    workPlaceId: string,
    systemTypeId: string | null,
    period: string | null,
    value: number | null,
    field: 'freeInterventionsCount' | 'freeHours' | 'freeInterventionsCountReset' | 'freeHoursReset'
  ) => {
    if (!reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions) return;

    const updatedInterventions = reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.map(
      (intervention) => {
        const isMatch =
          (intervention.workPlaceId === workPlaceId &&
            (interventionLevel === 'workPlace' ||
              (interventionLevel === 'workPlacePeriod' && intervention.period === period) ||
              (interventionLevel === 'workPlaceSystemTypePeriod' &&
                intervention.systemTypeId === systemTypeId &&
                intervention.period === period) ||
              (interventionLevel === 'systemType' && intervention.systemTypeId === systemTypeId))) ||
          (intervention.systemTypeId === systemTypeId && interventionLevel === 'systemType');

        if (!isMatch) return intervention;

        const updated = {
          ...intervention,
          [field]: value ?? 0,
        };

        // Auto-update the reset fields if the main ones are changed
        if (field === 'freeInterventionsCount') {
          updated.freeInterventionsCountReset = value ?? 0;
        }
        if (field === 'freeHours') {
          updated.freeHoursReset = value ?? 0;
        }

        return updated;
      }
    );

    dispatch(
      updateField({
        field: 'contractInterventionSetting',
        value: {
          ...reduxContract.contractInterventionSetting,
          contractId: reduxContract.id ?? '',
          contractWorkPlaceInterventions: updatedInterventions,
        },
      })
    );
  };

  const applyGlobalValue = () => {
    if (!reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions || globalValue === null) return;

    const isFreeIntervention = reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4;

    const field = isFreeIntervention ? 'freeInterventionsCount' : 'freeHours';
    const resetField = isFreeIntervention ? 'freeInterventionsCountReset' : 'freeHoursReset';

    const updatedInterventions = reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.map(
      (intervention) => ({
        ...intervention,
        [field]: globalValue,
        [resetField]: globalValue,
      })
    );

    dispatch(
      updateField({
        field: 'contractInterventionSetting',
        value: {
          ...reduxContract.contractInterventionSetting,
          contractId: reduxContract.id ?? '',
          contractWorkPlaceInterventions: updatedInterventions,
        },
      })
    );
  };

  const handleInterventionLevelChange = (
    newLevel: 'contract' | 'workPlace' | 'systemType' | 'workPlacePeriod' | 'workPlaceSystemTypePeriod'
  ) => {
    setInterventionLevel(newLevel);

    // Get selected workplaces from the form
    const selectedWorkPlaceNames = reduxContract.contractInterventionSetting.contractWorkPlaceInterventions || [];
    const selectedWorkPlaces = reduxContract.company.workPlaces.filter((w: any) =>
      selectedWorkPlaceNames.includes(w.name)
    );

    // Create new interventions based on the selected level
    let newInterventions: any[] = [];

    if (newLevel === 'workPlace') {
      // One intervention per selected workplace
      newInterventions = selectedWorkPlaces.map((workPlace: any) => ({
        id: '',
        contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
        workPlaceId: workPlace.id,
        workPlace: workPlace,
        systemTypeId: '',
        freeInterventionsCount: 0,
        usedInterventionsCount: 0,
        freeHours: 0,
        usedHours: 0,
      }));
    } else if (newLevel === 'systemType') {
      // Get unique system types from contractWorkPlaceItems for selected workplaces
      const uniqueSystemTypes = new Map<string, any>();
      reduxContract.contractWorkPlaceItems.forEach((item: any) => {
        const sysId = item.systemType?.id;
        if (sysId && !uniqueSystemTypes.has(sysId)) {
          uniqueSystemTypes.set(sysId, item.systemType);
        }
      });
      newInterventions = Array.from(uniqueSystemTypes.values());
    } else if (newLevel === 'workPlacePeriod') {
      // For each selected workplace, find all unique periods from contractWorkPlaceItems
      const uniqueCombinations = new Map();
      selectedWorkPlaces.forEach((workPlace: any) => {
        const periods = Array.from(
          new Set(
            reduxContract.contractWorkPlaceItems
              .filter((item: any) => item.workPlace?.id === workPlace.id)
              .map((item: any) => item.period)
          )
        ).filter(Boolean);

        periods.forEach((period: string) => {
          const key = `${workPlace.id}-${period}`;
          if (!uniqueCombinations.has(key)) {
            uniqueCombinations.set(key, {
              id: '',
              contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
              workPlaceId: workPlace.id,
              workPlace: workPlace,
              period,
              freeInterventionsCount: 0,
              usedInterventionsCount: 0,
              freeHours: 0,
              usedHours: 0,
            });
          }
        });
      });
      newInterventions = Array.from(uniqueCombinations.values());
    } else if (newLevel === 'workPlaceSystemTypePeriod') {
      // Get unique combinations of workplace + system type + period from contractWorkPlaceItems for selected workplaces
      const uniqueCombinations = new Map();
      reduxContract.contractWorkPlaceItems.forEach((item: any) => {
        if (selectedWorkPlaces.some((wp: any) => wp.id === item.workPlace?.id)) {
          const key = `${item.workPlace.id}-${item.systemType.id}-${item.period}`;
          if (!uniqueCombinations.has(key)) {
            uniqueCombinations.set(key, {
              id: '',
              contractInterventionSettingId: reduxContract.contractInterventionSetting?.id ?? '',
              workPlaceId: item.workPlace.id,
              workPlace: item.workPlace,
              systemTypeId: item.systemType.id,
              systemType: item.systemType,
              period: item.period,
              freeInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
              usedInterventionsCount: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
              freeHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
              usedHours: reduxContract.contractInterventionSetting.contractWorkPlaceInterventions.find(
                (cwpi: any) => cwpi.workPlaceId == item?.workPlace?.id && cwpi.systemTypeId == item?.systemType?.id
              )?.usedHours,
            });
          }
        }
      });
      newInterventions = Array.from(uniqueCombinations.values());
    }

    // Update Redux with new interventions and level
    dispatch(
      updateField({
        field: 'contractInterventionSetting',
        value: {
          ...reduxContract.contractInterventionSetting,
          contractId: reduxContract.id ?? '',
          interventionLevel: newLevel,
          contractWorkPlaceInterventions: newInterventions,
        },
      })
    );
  };

  return (
    <Form form={form} layout='vertical' className='modern-form'>
      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Form.Item label={<span className='form-label'>Tip Tarifare</span>}>
            <AutoComplete
              options={contractInterventionTariffTypes?.map((item: any) => ({ value: item?.name }))}
              onSelect={handleTariffChange}
              value={reduxContract.contractInterventionSetting?.enumContractInterventionTariffType?.name}
              className='modern-input'
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          {(reduxContract?.contractInterventionSetting?.enumContractInterventionTariffTypeId === 3 ||
            reduxContract?.contractInterventionSetting?.enumContractInterventionTariffTypeId === 4) && (
            <Form.Item label={<span className='form-label'>Tarif Personalizat (RON/oră)</span>}>
              <InputNumber
                min={0}
                value={reduxContract.contractInterventionSetting?.customHourlyRate ?? 0}
                onChange={(value) =>
                  dispatch(
                    updateField({
                      field: 'contractInterventionSetting',
                      value: {
                        ...reduxContract.contractInterventionSetting,
                        contractId: reduxContract.id ?? '',
                        customHourlyRate: value,
                      },
                    })
                  )
                }
                className='modern-input'
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}
        </Col>

        <Col span={24}>
          <Form.Item label={<span className='form-label'>Tip Intervenții</span>}>
            <AutoComplete
              options={contractInterventionTypes?.map((item: any) => ({ value: item?.name }))}
              onSelect={handleInterventionTypeChange}
              value={reduxContract.contractInterventionSetting?.enumContractInterventionType?.name}
              className='modern-input'
            />
          </Form.Item>
        </Col>

        {(reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 3 ||
          reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4) && (
          <Col span={24}>
            <Card className='intervention-card'>
              <Form.Item label={<span className='form-label'>Nivel de aplicare a intervențiilor</span>}>
                <Radio.Group
                  value={interventionLevel}
                  onChange={(e) => handleInterventionLevelChange(e.target.value)}
                  className='modern-radio-group'
                >
                  <Radio value='contract'>Contract</Radio>
                  <Radio value='workPlace'>Punct de lucru</Radio>
                  <Radio value='systemType'>Sistem</Radio>
                  <Radio value='workPlacePeriod'>Punct de lucru + Periodicitate</Radio>
                  <Radio value='workPlaceSystemTypePeriod'>Punct de lucru + Sistem + Periodicitate</Radio>
                </Radio.Group>
              </Form.Item>
              {interventionLevel === 'contract' && (
                <Space direction='horizontal' className='contract-space'>
                  <Form.Item
                    label={
                      <span className='form-label'>
                        {reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                          ? 'Numar intervenții'
                          : 'Limita de ore gratuite'}
                      </span>
                    }
                  >
                    <InputNumber
                      min={0}
                      value={
                        reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 3
                          ? reduxContract?.contractInterventionSetting?.freeHoursLimit ?? 0
                          : reduxContract?.contractInterventionSetting?.freeInterventionsCount ?? 0
                      }
                      onChange={(value) =>
                        dispatch(
                          updateField({
                            field: 'contractInterventionSetting',
                            value: {
                              ...reduxContract?.contractInterventionSetting,
                              contractId: reduxContract.id ?? '',
                              [reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 3
                                ? 'freeHoursLimit'
                                : 'freeInterventionsCount']: value ?? 0,
                            },
                          })
                        )
                      }
                      className='modern-input'
                    />
                  </Form.Item>
                </Space>
              )}
              {interventionLevel !== 'contract' && (
                <>
                  <Form.Item
                    label={<span className='form-label'>Puncte de Lucru</span>}
                    rules={[{ required: true, message: 'Selectați cel puțin un punct de lucru!' }]}
                  >
                    <Select
                      mode='multiple'
                      allowClear
                      disabled={interventionLevel === 'systemType'}
                      onChange={handleWorkPlaceChange}
                      value={(() => {
                        if (interventionLevel === 'systemType') {
                          return Array.from(
                            new Set(reduxContract.contractWorkPlaceItems.map((item: any) => item.workPlace?.name))
                          ).filter(Boolean);
                        }
                        return Array.from(
                          new Set(
                            reduxContract.contractInterventionSetting.contractWorkPlaceInterventions
                              .map((cw: any) => cw.workPlace?.name)
                              .filter((name) => !!name)
                          )
                        );
                      })()}
                      optionLabelProp='label'
                      className='modern-select'
                      style={{ width: '100%' }}
                    >
                      <Select.Option key='all' value='all'>
                        Selectează Toate
                      </Select.Option>
                      {allWorkplaces.map((name: string) => (
                        <Select.Option key={name} value={name} label={name}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Space direction='horizontal' className='global-value-space'>
                    <InputNumber
                      min={0}
                      placeholder={
                        reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                          ? 'Nr. intervenții'
                          : 'Nr. ore'
                      }
                      value={globalValue}
                      onChange={(value: any) => setGlobalValue(value)}
                      className='modern-input'
                    />
                    <Button type='primary' onClick={applyGlobalValue} className='modern-button'>
                      Aplică la toate
                    </Button>
                  </Space>
                </>
              )}

              {interventionLevel === 'workPlace' && (
                <Row gutter={[24, 24]}>
                  {(() => {
                    const allowedWpIds = new Set(
                      reduxContract.contractInterventionSetting?.contractWorkPlaceInterventions?.map(
                        (i: any) => i.workPlaceId
                      )
                    );

                    const uniqueItemsMap = new Map<string, any>();

                    (reduxContract.contractWorkPlaceItems || [])
                      .filter((item: any) => allowedWpIds.has(item.workPlace?.id))
                      .forEach((item: any) => {
                        const key = `${item.workPlace.id}`;
                        if (!uniqueItemsMap.has(key)) {
                          uniqueItemsMap.set(key, item);
                        }
                      });

                    return Array.from(uniqueItemsMap.values()).map((item: any) => {
                      const key = `${item.workPlace.id}`;
                      const intervention =
                        reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.find(
                          (i: any) => i.workPlaceId === item.workPlace.id
                        );
                      return (
                        <Col xs={24} sm={24} md={12} key={key}>
                          <Card
                            className='modern-card'
                            style={{
                              height: '100%',
                              borderRadius: '12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              transition: 'all 0.3s ease',
                            }}
                            hoverable
                          >
                            <div className='card-header'>
                              <Typography.Title level={5} style={{ margin: 0, color: '#1a1a1a' }}>
                                {item.workPlace.name}
                              </Typography.Title>
                            </div>
                            <div className='input-group'>
                              <Typography.Text strong className='input-label'>
                                {reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                  ? 'Număr intervenții'
                                  : 'Număr ore'}
                              </Typography.Text>
                              <InputNumber
                                min={0}
                                placeholder={
                                  reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                    ? 'Nr. intervenții'
                                    : 'Nr. ore'
                                }
                                value={
                                  reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                    ? intervention?.freeInterventionsCount ?? 0
                                    : intervention?.freeHours ?? 0
                                }
                                onChange={(value) =>
                                  handleWorkPlaceInterventionChange(
                                    item.workPlaceId,
                                    '',
                                    null,
                                    value,
                                    reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                      ? 'freeInterventionsCount'
                                      : 'freeHours'
                                  )
                                }
                                className='modern-input'
                                style={{ width: '100%' }}
                              />
                            </div>
                          </Card>
                        </Col>
                      );
                    });
                  })()}
                </Row>
              )}
              {interventionLevel === 'systemType' && (
                <Row gutter={[24, 24]}>
                  {(() => {
                    const uniqueSystemTypes = new Map<string, any>();
                    reduxContract.contractWorkPlaceItems.forEach((item: any) => {
                      const sysId = item.systemType?.id;
                      if (sysId && !uniqueSystemTypes.has(sysId)) {
                        uniqueSystemTypes.set(sysId, item.systemType);
                      }
                    });

                    return Array.from(uniqueSystemTypes.entries()).map(([sysId, systemType]: [string, any]) => {
                      const intervention =
                        reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.find(
                          (i: any) => i.systemTypeId === sysId
                        );

                      return (
                        <Col xs={24} sm={24} md={12} key={`system-type-${sysId}`}>
                          <Card
                            className='modern-card'
                            style={{
                              height: '100%',
                              borderRadius: '12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              transition: 'all 0.3s ease',
                            }}
                            hoverable
                          >
                            <div className='card-header'>
                              <Typography.Title level={5} style={{ margin: 0, color: '#1a1a1a' }}>
                                {systemType.name}
                              </Typography.Title>
                            </div>
                            <div className='input-group'>
                              <Typography.Text strong className='input-label'>
                                {reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                  ? 'Număr intervenții'
                                  : 'Număr ore'}
                              </Typography.Text>
                              <InputNumber
                                min={0}
                                placeholder={
                                  reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                    ? 'Nr. intervenții'
                                    : 'Nr. ore'
                                }
                                value={
                                  reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                    ? intervention?.freeInterventionsCount ?? 0
                                    : intervention?.freeHours ?? 0
                                }
                                onChange={(value) =>
                                  handleWorkPlaceInterventionChange(
                                    '', // workPlaceId not needed for systemType
                                    sysId,
                                    null,
                                    value,
                                    reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4
                                      ? 'freeInterventionsCount'
                                      : 'freeHours'
                                  )
                                }
                                className='modern-input'
                                style={{ width: '100%' }}
                              />
                            </div>
                          </Card>
                        </Col>
                      );
                    });
                  })()}
                </Row>
              )}
              {interventionLevel === 'workPlacePeriod' && (
                <Row gutter={[24, 24]}>
                  {(() => {
                    const allowedWpIds = new Set(
                      reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.map(
                        (i: any) => i.workPlaceId
                      )
                    );

                    const uniqueItemsMap = new Map<string, any>();

                    (reduxContract.contractWorkPlaceItems || [])
                      .filter((item: any) => allowedWpIds.has(item.workPlace?.id))
                      .forEach((item: any) => {
                        const key = `${item.workPlace.id}-${item.period}`;
                        if (!uniqueItemsMap.has(key)) {
                          uniqueItemsMap.set(key, item);
                        }
                      });

                    return Array.from(uniqueItemsMap.values()).map((item: any) => {
                      const key = `${item.workPlace.id}-${item.period}`;
                      const intervention =
                        reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.find(
                          (i: any) => i.workPlaceId === item.workPlace.id && i.period === item.period
                        );
                      const isIntervention =
                        reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4;

                      return (
                        <Col xs={24} sm={24} md={12} key={key}>
                          <Card
                            className='modern-card'
                            style={{
                              height: '100%',
                              borderRadius: '12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              transition: 'all 0.3s ease',
                            }}
                            hoverable
                          >
                            <div className='card-header'>
                              <Typography.Title level={5} style={{ margin: 0, color: '#1a1a1a' }}>
                                {item.workPlace.name}
                              </Typography.Title>
                              <Typography.Text type='secondary' className='card-subtitle'>
                                {item.period}
                              </Typography.Text>
                            </div>

                            <Space direction='vertical' size='large' style={{ width: '100%' }}>
                              <div className='input-group'>
                                <Typography.Text strong className='input-label'>
                                  {isIntervention ? 'Număr intervenții' : 'Număr ore'}
                                </Typography.Text>
                                <InputNumber
                                  min={0}
                                  placeholder={isIntervention ? 'Nr. intervenții' : 'Nr. ore'}
                                  value={
                                    isIntervention
                                      ? intervention?.freeInterventionsCount ?? 0
                                      : intervention?.freeHours ?? 0
                                  }
                                  onChange={(value) =>
                                    handleWorkPlaceInterventionChange(
                                      item.workPlaceId,
                                      null,
                                      item.period,
                                      value,
                                      isIntervention ? 'freeInterventionsCount' : 'freeHours'
                                    )
                                  }
                                  className='modern-input'
                                  style={{ width: '100%' }}
                                />
                              </div>

                              <div className='input-group'>
                                <Typography.Text strong className='input-label'>
                                  Resetare recurentă
                                </Typography.Text>
                                <InputNumber
                                  min={0}
                                  placeholder={isIntervention ? 'Nr. resetare intervenții' : 'Nr. resetare ore'}
                                  value={
                                    isIntervention
                                      ? intervention?.freeInterventionsCountReset ?? 0
                                      : intervention?.freeHoursReset ?? 0
                                  }
                                  onChange={(value) =>
                                    handleWorkPlaceInterventionChange(
                                      item.workPlaceId,
                                      item.systemType.id,
                                      item.period,
                                      value,
                                      isIntervention ? 'freeInterventionsCountReset' : 'freeHoursReset'
                                    )
                                  }
                                  disabled={!!reduxContract?.id}
                                  className='modern-input'
                                  style={{ width: '100%' }}
                                />
                              </div>
                            </Space>
                          </Card>
                        </Col>
                      );
                    });
                  })()}
                </Row>
              )}
              {interventionLevel === 'workPlaceSystemTypePeriod' && (
                <Row gutter={[24, 24]}>
                  {(() => {
                    const allowedWpIds = new Set(
                      reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.map(
                        (i: any) => i.workPlaceId
                      )
                    );

                    const uniqueItemsMap = new Map<string, any>();

                    (reduxContract.contractWorkPlaceItems || [])
                      .filter((item: any) => allowedWpIds.has(item.workPlace?.id))
                      .forEach((item: any) => {
                        const key = `${item.workPlace.id}-${item.systemType.id}-${item.period}`;
                        if (!uniqueItemsMap.has(key)) {
                          uniqueItemsMap.set(key, item);
                        }
                      });

                    return Array.from(uniqueItemsMap.values()).map((item: any) => {
                      const key = `${item.workPlace.id}-${item.systemType.id}-${item.period}`;
                      const intervention =
                        reduxContract?.contractInterventionSetting?.contractWorkPlaceInterventions?.find(
                          (i: any) =>
                            i.workPlaceId === item.workPlace.id &&
                            i.systemTypeId === item.systemType.id &&
                            i.period === item.period
                        );
                      const isIntervention =
                        reduxContract?.contractInterventionSetting?.enumContractInterventionTypeId === 4;

                      return (
                        <Col xs={24} sm={24} md={12} key={key}>
                          <Card
                            className='modern-card'
                            style={{
                              height: '100%',
                              borderRadius: '12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                              transition: 'all 0.3s ease',
                            }}
                            hoverable
                          >
                            <div className='card-header'>
                              <Typography.Title level={5} style={{ margin: 0, color: '#1a1a1a' }}>
                                {item.workPlace.name}
                              </Typography.Title>
                              <Typography.Text type='secondary' className='card-subtitle'>
                                {item.systemType.name} - {item.period}
                              </Typography.Text>
                            </div>

                            <Space direction='vertical' size='large' style={{ width: '100%' }}>
                              <div className='input-group'>
                                <Typography.Text strong className='input-label'>
                                  {isIntervention ? 'Număr intervenții' : 'Număr ore'}
                                </Typography.Text>
                                <InputNumber
                                  min={0}
                                  placeholder={isIntervention ? 'Nr. intervenții' : 'Nr. ore'}
                                  value={
                                    isIntervention
                                      ? intervention?.freeInterventionsCount ?? 0
                                      : intervention?.freeHours ?? 0
                                  }
                                  onChange={(value) =>
                                    handleWorkPlaceInterventionChange(
                                      item.workPlaceId,
                                      item.systemType.id,
                                      item.period,
                                      value,
                                      isIntervention ? 'freeInterventionsCount' : 'freeHours'
                                    )
                                  }
                                  className='modern-input'
                                  style={{ width: '100%' }}
                                />
                              </div>

                              <div className='input-group'>
                                <Typography.Text strong className='input-label'>
                                  Resetare recurentă
                                </Typography.Text>
                                <InputNumber
                                  min={0}
                                  placeholder={isIntervention ? 'Nr. resetare intervenții' : 'Nr. resetare ore'}
                                  value={
                                    isIntervention
                                      ? intervention?.freeInterventionsCountReset ?? 0
                                      : intervention?.freeHoursReset ?? 0
                                  }
                                  onChange={(value) =>
                                    handleWorkPlaceInterventionChange(
                                      item.workPlaceId,
                                      item.systemType.id,
                                      item.period,
                                      value,
                                      isIntervention ? 'freeInterventionsCountReset' : 'freeHoursReset'
                                    )
                                  }
                                  disabled={!!reduxContract?.id}
                                  className='modern-input'
                                  style={{ width: '100%' }}
                                />
                              </div>
                            </Space>
                          </Card>
                        </Col>
                      );
                    });
                  })()}
                </Row>
              )}
            </Card>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default InterventiiTab;
